import { SpinnerIcon } from '@phosphor-icons/react/dist/ssr';

import { AuthBackground } from '@/components/auth';
import { TorianFullLogo } from '@/components/icons/torian-full-logo';
import { cn } from '@/lib/utils';

/**
 * Loading screen ported 1:1 from affiliate-torian-market/auth-loading-screen.
 * Renders the same AuthBackground + a centered card with a halo spinner,
 * title/description copy, an indeterminate progress bar and status chips
 * that signal which steps are running.
 *
 * Default copy is bot-context (session / wallet / Polymarket), but every
 * piece is overridable via props so screens can plug their own stage
 * (e.g. "Quoting withdrawal").
 */
export function AuthLoadingScreen({
  title = 'Preparing your wallet',
  description = 'Authenticating your Telegram session and syncing your Polymarket wallet.',
  shortDescription = 'Authenticating your session.',
  steps,
  className,
}: {
  title?: string;
  description?: string;
  shortDescription?: string;
  /** Override the trailing status chips. Defaults to session/wallet/Polymarket. */
  steps?: Array<{ label: string; color: string; delay?: string }>;
  className?: string;
}) {
  const stagedSteps =
    steps ??
    [
      { label: 'Session', color: 'bg-emerald-500' },
      { label: 'Wallet', color: 'bg-sky-500', delay: '0.4s' },
      { label: 'Polymarket', color: 'bg-primary', delay: '0.8s' },
    ];

  return (
    <div
      role="status"
      aria-busy="true"
      aria-live="polite"
      className={cn(
        'bg-background relative isolate flex min-h-svh w-full flex-col overflow-hidden select-none',
        className,
      )}
    >
      <AuthBackground />

      <div className="relative flex min-h-svh w-full flex-col">
        <section className="relative flex min-w-0 flex-1 items-center justify-center px-4 py-10 sm:px-6 sm:py-14 lg:py-16">
          <div className="auth-loading-pop flex w-full max-w-xl flex-col items-center gap-6 sm:gap-7">
            {/* Logo + ambient glow. */}
            <div className="relative inline-flex items-center justify-center">
              <span
                aria-hidden
                className="from-primary/40 absolute -inset-8 rounded-full bg-linear-to-br via-violet-500/20 to-sky-500/30 opacity-70 blur-2xl"
              />
              <TorianFullLogo className="text-foreground relative h-7 w-auto sm:h-8 md:h-9" />
            </div>

            {/* Card central com spinner + título + barra de progresso. */}
            <div
              className={cn(
                'bg-card/95 supports-backdrop-filter:bg-card/90 ring-foreground/10 shadow-foreground/5 relative flex w-full flex-col items-center gap-5 overflow-hidden rounded-3xl px-6 py-7 text-center shadow-2xl ring-1 backdrop-blur-2xl sm:px-9 sm:py-8',
              )}
            >
              <span
                aria-hidden
                className="from-primary/60 pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r via-violet-500/60 to-transparent"
              />
              <span
                aria-hidden
                className="from-primary/12 pointer-events-none absolute inset-x-0 top-0 -z-10 h-32 bg-linear-to-b via-transparent to-transparent"
              />

              {/* Spinner halo. */}
              <div className="relative inline-flex">
                <span
                  aria-hidden
                  className="absolute -inset-2.5 rounded-full bg-primary/25 opacity-60 blur-xl sm:-inset-3.5"
                />
                <span className="ring-primary/30 bg-primary/15 text-foreground relative inline-flex size-12 items-center justify-center rounded-2xl ring-1 sm:size-14">
                  <SpinnerIcon
                    weight="bold"
                    className="text-foreground size-6 animate-spin sm:size-7"
                  />
                </span>
              </div>

              <div className="flex flex-col items-center gap-1.5">
                <h1 className="text-foreground text-lg font-semibold tracking-tight sm:text-xl md:text-2xl">
                  {title}
                </h1>
                <p className="text-muted-foreground hidden max-w-md text-[13px] leading-relaxed sm:block sm:text-sm">
                  {description}
                </p>
                <p className="text-muted-foreground block max-w-xs text-[12px] leading-relaxed sm:hidden">
                  {shortDescription}
                </p>
              </div>

              {/* Indeterminate progress bar. */}
              <div
                className="bg-foreground/5 ring-foreground/10 relative h-1 w-full max-w-xs overflow-hidden rounded-full ring-1"
                aria-hidden
              >
                <span className="auth-loading-bar absolute inset-y-0 block w-1/3 rounded-full bg-linear-to-r from-sky-500/70 via-violet-500/80 to-primary" />
              </div>

              {/* Status chips. */}
              <ul className="text-muted-foreground flex flex-wrap items-center justify-center gap-1.5 pt-1 text-[10px] font-semibold tracking-[0.14em] uppercase sm:gap-2 sm:text-[11px]">
                {stagedSteps.map((step) => (
                  <li
                    key={step.label}
                    className="bg-foreground/5 ring-foreground/10 inline-flex items-center gap-1.5 rounded-full px-2 py-1 ring-1"
                  >
                    <span
                      className={cn('auth-loading-dot', step.color)}
                      style={step.delay ? { animationDelay: step.delay } : undefined}
                    />
                    {step.label}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <footer className="text-muted-foreground relative flex flex-wrap items-center justify-between gap-3 px-4 pb-6 text-[11px] sm:px-6 lg:px-10">
          <span className="tabular-nums">
            © {new Date().getFullYear()} Torian Market
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="auth-loading-dot bg-primary" />
            Telegram Mini App
          </span>
        </footer>
      </div>

      <span className="sr-only">{description}</span>
    </div>
  );
}
